import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {parse, subMonths} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import * as htmlToImage from "html-to-image";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
    showLayer: false,
    mapMode: 'routes-mode'
});

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    showLayer: "Exibir setor",
    mapModeTypeRoutes: "Rota",
    mapModeTypeHeatmap: "Mapa de calor",
    mapModeTypeDetail: "Detalhes da rota"
};

const buildInitialFilters = () => {
    const filters = initialFilters();

    const parseDate = (value) => {
        if (!value) return null;
        const parsed = parse(value, "dd/MM/yyyy HH:mm", new Date());
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    const urlStart = parseDate(ReportUtils.getUrlParam("dtStart"));
    const urlEnd = parseDate(ReportUtils.getUrlParam("dtEnd"));
    const garrisonId = ReportUtils.getUrlParam("garrisonId");

    return {
        ...filters,
        dtStart: urlStart ?? filters.dtStart,
        dtEnd: urlEnd ?? filters.dtEnd,
        garrisonId: garrisonId ?? null
    };
};

const GarrisonLocationMap = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [collapsed, setCollapsed] = useState(false);

    const [filters, setFilters] = useState(() => buildInitialFilters());

    const [mapImage, setMapImage] = useState(null);
    const [showMap, setShowMap] = useState(true);

    const [fixedTop, setFixedTop] = useState(null);
    const buttonRef = useRef(null);

    const title = 'Localização da guarnição';
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
                mapModeTypeRoutes: filters.mapMode === 'routes-mode' ? "Sim" : "Não",
                mapModeTypeHeatmap: filters.mapMode === 'heatmap-mode' ? "Sim" : "Não",
                mapModeTypeDetail: filters.mapMode === 'detail-mode' ? "Sim" : "Não",
                mapMode: null,
                garrisonId: null
            }, filterLabels)
        );

    }, [reportData]);

    const [applied, setApplied] = useState(false);

    useEffect(() => {
        if (!applied) return;
        apply();
    }, [filters.showLayer])

    useEffect(() => {
        if (filters.garrisonId === null || filters.garrisonId === '') return;
        apply();
    }, [filters.garrisonId])

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                showLayer: filters.showLayer ?? false,
                mapMode: filters.mapMode,
                garrisonId: filters.garrisonId
            };

            const auditFilters = ReportUtils.filters({
                    ...filters,
                    dtStart: formattedFilters.dtStart,
                    dtEnd: formattedFilters.dtEnd,
                    mapModeTypeRoutes: filters.mapMode === 'routes-mode' ? "Sim" : "Não",
                    mapModeTypeHeatmap: filters.mapMode === 'heatmap-mode' ? "Sim" : "Não",
                    mapModeTypeDetail: filters.mapMode === 'detail-mode' ? "Sim" : "Não",
                    mapMode: null,
                    garrisonId: null
            }, filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/garrison_location_map/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setApplied(true);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setLoading(false);
        }
    }

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        const filters = buildInitialFilters();
        setFilters(filters);
        setReportData(null);
        setApplied(false);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    const handlePrintAsImage = () => {
        ReportUtils.generateMapImage({
            nodeId: "map-report-component",
            setMapImage,
            setShowMap,
            printRef: printComponentRef,
            htmlToImageLib: htmlToImage,
            ignoreElements: ((el) => el.tagName === "IFRAME")
        });
    };

    return (
        <div className={"flex"}>

            <Template isLoading={loading}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            printImg={true}
                            onPrintImg={handlePrintAsImage}
                            headerChange={true}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={reportData?.result.coordinates.length}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                            setCollapsed={setCollapsed}
                        />
                    </div>
                </Template.Header>
                <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                    <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                          collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                        <Filters setFilters={setFilters} filters={filters}/>
                        <AutoDatetime setFilters={setFilters}/>

                    </Menu>
                </Template.Body>
            </Template>

            <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.result.coordinates.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.result.coordinates.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body>
                    <div className={`flex-1 avoid-transform ${!reportData || reportData?.result.coordinates.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData}/>
                        <NoDataContent reportData={reportData} data={reportData?.result.coordinates.length}/>
                        <Content reportData={reportData} data={reportData?.result.coordinates.length} showMap={showMap} showLayer={filters.showLayer} mapMode={filters.mapMode} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} mapImage={mapImage}/>
                    </div>
                </Card.Body>
            </Card>

        </div>
    );
};

export default GarrisonLocationMap;